import type { BookingInvitee } from '../../../../types/RoomBooking'
import { MAX_EXTERNAL_GUESTS } from '../../../../types/RoomBooking'

export { MAX_EXTERNAL_GUESTS }

export interface ExternalGuestData {
  name: string
  company: string
  email: string
  /** Carried through edits so an existing guest's RSVP isn't reset to pending. */
  status?: BookingInvitee['status']
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function normalizeEmail(email: string): string {
  return email.toLowerCase().trim()
}

export function isValidEmail(email: string): boolean {
  return EMAIL_RE.test(email.trim())
}

export interface GuestEmailContext {
  /** Emails of everyone in the company (members can't be invited as guests). */
  companyEmails: ReadonlySet<string>
  /** Emails already on this booking: internal invitees and guests added so far. */
  takenEmails: ReadonlySet<string>
}

/** Why this email can't be added as a guest, or null if it can. Sets hold normalized emails. */
export function guestEmailError(email: string, ctx: GuestEmailContext): string | null {
  if (!isValidEmail(email)) return 'Please enter a valid email address'
  const key = normalizeEmail(email)
  if (ctx.companyEmails.has(key)) return 'This person is a company member — add them from Invitees'
  if (ctx.takenEmails.has(key)) return 'This guest has already been added'
  return null
}

/**
 * The email to offer as "Add as external guest" for what the user typed in the Invitees
 * search, or null when the text isn't a usable guest email.
 */
export function externalGuestSuggestion(input: string, ctx: GuestEmailContext): string | null {
  const email = input.trim()
  return guestEmailError(email, ctx) === null ? email : null
}

export function addGuestsButtonLabel(guestCount: number): string {
  return guestCount > 1 ? 'Add Guests' : 'Add Guest'
}

export function guestLimitReason(guestCount: number): string {
  return guestCount >= MAX_EXTERNAL_GUESTS ? `A meeting can have at most ${MAX_EXTERNAL_GUESTS} external guests` : ''
}

/** A stored booking keeps both kinds in one `invitees` array; the form edits them separately. */
export function splitInvitees(invitees: BookingInvitee[]): { internal: BookingInvitee[]; external: ExternalGuestData[] } {
  const internal: BookingInvitee[] = []
  const external: ExternalGuestData[] = []
  for (const inv of invitees) {
    if (inv.type === 'external' && inv.email) {
      external.push({ name: inv.name || '', company: inv.company || '', email: inv.email, status: inv.status })
    } else {
      internal.push(inv)
    }
  }
  return { internal, external }
}

export function toExternalInvitee(guest: ExternalGuestData): BookingInvitee {
  return {
    email: guest.email,
    name: guest.name,
    company: guest.company,
    type: 'external',
    status: guest.status || 'pending',
  }
}
